import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Ship, CarFront } from 'lucide-react'
import { createTripFromTemplate } from '../../shared/api/marketplace'
import { Button } from '../../shared/components/Button'
import { Spinner } from '../../shared/components/Spinner'
import { CruiseCreate } from './CruiseCreate'
import { RoadTripCreate } from './RoadTripCreate'

const TEMPLATE_META = {
  cruise: {
    Icon: Ship,
    title: 'Cruise template',
    description: 'Embarkation, port days, sea days, and disembarkation — ready to customize.',
    defaultTitle: 'Mediterranean Cruise',
    defaultDestination: 'Mediterranean',
  },
  'road-trip': {
    Icon: CarFront,
    title: 'Road trip template',
    description: 'Car pick-up, scenic drives, overnight stops, and return — built for multi-day loops.',
    defaultTitle: 'Scenic Road Trip',
    defaultDestination: 'Tuscany, Italy',
  },
} as const

export function CreateTripFromTemplate() {
  const { template = 'cruise' } = useParams<{ template: string }>()
  const meta = TEMPLATE_META[template as keyof typeof TEMPLATE_META] ?? TEMPLATE_META.cruise
  const navigate = useNavigate()

  const [title, setTitle] = useState<string>(meta.defaultTitle)
  const [destination, setDestination] = useState<string>(meta.defaultDestination)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const apiTemplate = template === 'road-trip' ? 'road_trip' : 'cruise'
      const trip = await createTripFromTemplate({
        template: apiTemplate,
        title: title.trim(),
        destination: destination.trim(),
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      })
      navigate(`/trips/${trip.id}/places`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create trip')
    } finally {
      setBusy(false)
    }
  }

  const { Icon } = meta

  if (template === 'cruise') return <CruiseCreate />
  if (template === 'road-trip') return <RoadTripCreate />

  return (
    <div className="mx-auto max-w-xl">
      <Link
        to="/trips/new"
        className="mb-4 inline-flex items-center gap-1 text-sm text-st-muted hover:text-st-text"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </Link>

      <div className="mb-5 flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-st-md bg-st-primary/10 text-st-primary">
          <Icon className="h-5 w-5" />
        </span>
        <div>
          <h1 className="font-display text-2xl font-bold text-st-text">{meta.title}</h1>
          <p className="text-sm text-st-muted">{meta.description}</p>
        </div>
      </div>

      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4 rounded-st-lg border border-st-line bg-st-surface p-5">
        <Field label="Trip title">
          <input value={title} onChange={(e) => setTitle(e.target.value)} className="input" />
        </Field>
        <Field label="Destination">
          <input value={destination} onChange={(e) => setDestination(e.target.value)} className="input" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Start date">
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="input" />
          </Field>
          <Field label="End date">
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="input" />
          </Field>
        </div>
        {error && <p className="text-sm text-st-danger">{error}</p>}
        <Button variant="accent" type="submit" disabled={busy}>
          {busy ? <Spinner label="Creating…" /> : 'Create from template'}
        </Button>
      </form>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-st-muted">{label}</span>
      {children}
    </label>
  )
}
